import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { networkManager } from '../api/NetworkManager';
import { ArrowLeft, Plus, X, Loader2 } from 'lucide-react';

interface Theme {
  name: string;
  description: string;
}

interface Criterion {
  name: string;
  weight: number;
  maxScore: number;
}

export function EventCreatePage() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [submissionDeadline, setSubmissionDeadline] = useState('');
  const [themes, setThemes] = useState<Theme[]>([{ name: '', description: '' }]);
  const [criteria, setCriteria] = useState<Criterion[]>([
    { name: 'Innovation', weight: 30, maxScore: 10 },
    { name: 'Feasibility', weight: 25, maxScore: 10 },
    { name: 'Impact', weight: 25, maxScore: 10 },
    { name: 'Presentation', weight: 20, maxScore: 10 },
  ]);
  const [minTeamSize, setMinTeamSize] = useState(2);
  const [maxTeamSize, setMaxTeamSize] = useState(5);
  const [timerDuration, setTimerDuration] = useState(300);
  const [allowSelfJoin, setAllowSelfJoin] = useState(true);
  const [enableMentors, setEnableMentors] = useState(true);
  const [isTest, setIsTest] = useState(false);

  const totalWeight = criteria.reduce((sum, c) => sum + c.weight, 0);

  function validate(): string | null {
    if (name.trim().length < 3) return 'Event name must be at least 3 characters';
    if (name.trim().length > 100) return 'Event name must be 100 characters or fewer';

    const validThemes = themes.filter((t) => t.name.trim());
    if (validThemes.length === 0) return 'At least one theme is required';
    if (validThemes.length > 10) return 'Maximum 10 themes allowed';

    const validCriteria = criteria.filter((c) => c.name.trim());
    if (validCriteria.length === 0) return 'At least one scoring criterion is required';
    if (totalWeight !== 100) return 'Scoring criteria weights must sum to 100';

    if (!startDate || !endDate || !submissionDeadline) return 'All dates are required';

    const start = new Date(startDate);
    const deadline = new Date(submissionDeadline);
    const end = new Date(endDate);

    if (start >= deadline) return 'Start date must be before submission deadline';
    if (deadline >= end) return 'Submission deadline must be before end date';

    if (minTeamSize > maxTeamSize) return 'Min team size must be ≤ max team size';

    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const res = await networkManager.post<{ status: boolean; data: { eventId: string } }>(
        '/api/events',
        {
          name: name.trim(),
          description: description.trim(),
          startDate: new Date(startDate).toISOString(),
          endDate: new Date(endDate).toISOString(),
          submissionDeadline: new Date(submissionDeadline).toISOString(),
          themes: themes.filter((t) => t.name.trim()),
          scoringCriteria: criteria.filter((c) => c.name.trim()),
          settings: { minTeamSize, maxTeamSize, timerDurationSeconds: timerDuration, allowSelfJoin, enableMentors },
          isTest,
        },
      );
      navigate(`/events/${res.data.eventId}`);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to create event';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="animate-fade-in">
      <button onClick={() => navigate(-1)} className="btn-ghost mb-4">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <h1 className="font-display text-3xl font-bold text-ink">Create Event</h1>

      {error && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-6 space-y-8">
        {/* Basics */}
        <section className="card p-5">
          <h2 className="font-semibold text-ink">Basics</h2>
          <div className="mt-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-ink">Name *</label>
              <input
                type="text"
                className="input-base mt-1"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="VN Hackathon 2026"
                required
                maxLength={100}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">Description</label>
              <textarea
                className="input-base mt-1"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Build the future of Byte"
                maxLength={2000}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="text-sm font-medium text-ink">Start Date *</label>
                <input
                  type="datetime-local"
                  className="input-base mt-1"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-ink">Submission Deadline *</label>
                <input
                  type="datetime-local"
                  className="input-base mt-1"
                  value={submissionDeadline}
                  onChange={(e) => setSubmissionDeadline(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-ink">End Date *</label>
                <input
                  type="datetime-local"
                  className="input-base mt-1"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>
        </section>

        {/* Themes */}
        <section className="card p-5">
          <h2 className="font-semibold text-ink">Themes</h2>
          <div className="mt-4 space-y-3">
            {themes.map((theme, i) => (
              <div key={i} className="flex gap-3">
                <div className="flex-1 space-y-2">
                  <input
                    type="text"
                    className="input-base"
                    value={theme.name}
                    onChange={(e) => {
                      const t = [...themes];
                      t[i] = { ...t[i], name: e.target.value };
                      setThemes(t);
                    }}
                    placeholder="Theme name"
                  />
                  <input
                    type="text"
                    className="input-base"
                    value={theme.description}
                    onChange={(e) => {
                      const t = [...themes];
                      t[i] = { ...t[i], description: e.target.value };
                      setThemes(t);
                    }}
                    placeholder="Theme description"
                  />
                </div>
                {themes.length > 1 && (
                  <button
                    type="button"
                    className="btn-ghost self-start text-ink-tertiary"
                    onClick={() => setThemes(themes.filter((_, j) => j !== i))}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => setThemes([...themes, { name: '', description: '' }])}
            >
              <Plus className="h-4 w-4" /> Add Theme
            </button>
          </div>
        </section>

        {/* Scoring Criteria */}
        <section className="card p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-ink">Scoring Criteria</h2>
            <span
              className={`text-sm font-medium ${totalWeight === 100 ? 'text-green-600' : 'text-red-600'}`}
            >
              Total: {totalWeight}%
            </span>
          </div>
          <div className="mt-4 space-y-3">
            {criteria.map((c, i) => (
              <div key={i} className="flex items-center gap-3">
                <input
                  type="text"
                  className="input-base flex-1"
                  value={c.name}
                  onChange={(e) => {
                    const cr = [...criteria];
                    cr[i] = { ...cr[i], name: e.target.value };
                    setCriteria(cr);
                  }}
                  placeholder="Criterion name"
                />
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    className="input-base w-20 text-center"
                    value={c.weight}
                    onChange={(e) => {
                      const cr = [...criteria];
                      cr[i] = { ...cr[i], weight: parseInt(e.target.value) || 0 };
                      setCriteria(cr);
                    }}
                    min={1}
                    max={100}
                  />
                  <span className="text-sm text-ink-secondary">%</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-ink-secondary">Max</span>
                  <input
                    type="number"
                    className="input-base w-16 text-center"
                    value={c.maxScore}
                    onChange={(e) => {
                      const cr = [...criteria];
                      cr[i] = { ...cr[i], maxScore: parseInt(e.target.value) || 10 };
                      setCriteria(cr);
                    }}
                    min={1}
                    max={100}
                  />
                </div>
                {criteria.length > 1 && (
                  <button
                    type="button"
                    className="btn-ghost text-ink-tertiary"
                    onClick={() => setCriteria(criteria.filter((_, j) => j !== i))}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => setCriteria([...criteria, { name: '', weight: 0, maxScore: 10 }])}
            >
              <Plus className="h-4 w-4" /> Add Criterion
            </button>
          </div>
        </section>

        {/* Settings */}
        <section className="card p-5">
          <h2 className="font-semibold text-ink">Settings</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <div>
              <label className="text-sm font-medium text-ink">Min Team Size</label>
              <input
                type="number"
                className="input-base mt-1"
                value={minTeamSize}
                onChange={(e) => setMinTeamSize(parseInt(e.target.value) || 2)}
                min={1}
                max={20}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">Max Team Size</label>
              <input
                type="number"
                className="input-base mt-1"
                value={maxTeamSize}
                onChange={(e) => setMaxTeamSize(parseInt(e.target.value) || 5)}
                min={1}
                max={50}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">Timer (seconds per team)</label>
              <input
                type="number"
                className="input-base mt-1"
                value={timerDuration}
                onChange={(e) => setTimerDuration(parseInt(e.target.value) || 300)}
                min={60}
                max={1800}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between rounded-lg border border-ring p-3">
            <div>
              <label className="text-sm font-medium text-ink">Allow Self-Join</label>
              <p className="text-xs text-ink-tertiary">When off, only admins/organizers can add users to teams</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={allowSelfJoin}
              onClick={() => setAllowSelfJoin(!allowSelfJoin)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${allowSelfJoin ? 'bg-accent' : 'bg-gray-300'}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${allowSelfJoin ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
          <div className="mt-4 flex items-center justify-between rounded-lg border border-ring p-3">
            <div>
              <label className="text-sm font-medium text-ink">Enable Mentors</label>
              <p className="text-xs text-ink-tertiary">When off, mentor feedback features are hidden for this event</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={enableMentors}
              onClick={() => setEnableMentors(!enableMentors)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${enableMentors ? 'bg-accent' : 'bg-gray-300'}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${enableMentors ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
          <div className={`mt-4 flex items-center justify-between rounded-lg border p-3 ${isTest ? 'border-purple-300 bg-purple-50' : 'border-ring'}`}>
            <div>
              <label className="text-sm font-medium text-ink">Mark as Test Event</label>
              <p className="text-xs text-ink-tertiary">Test events are hidden by default — only visible to admins who enable "Show test events"</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isTest}
              onClick={() => setIsTest(!isTest)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${isTest ? 'bg-purple-500' : 'bg-gray-300'}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${isTest ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
        </section>

        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving || totalWeight !== 100}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {saving ? 'Creating...' : 'Create Event'}
          </button>
        </div>
      </form>
    </div>
  );
}
